import type { Lorebook, LorebookEntry } from '@/types/domain'

export const RECENT_MATCH_MESSAGE_LIMIT = 24

interface MessageLike {
  is_user_author?: boolean
  content?: string
}

interface CharacterLike {
  lorebooks?: Lorebook[]
  lorebook?: Lorebook | null
}

interface BuildLorebookSectionsOptions {
  lorebooks?: Lorebook[]
  lorebook?: Lorebook | null
  messages?: MessageLike[]
  userMessage?: string
  contextSize?: number
  estimateTokens?: (text: string) => number
}

interface BuildLorebookSectionsResult {
  scenarioText: string
  defaultText: string
  matchedEntries: LorebookEntry[]
}

export function normalizeLorebookMatchText(text = ''): string {
  return text.toString().toLowerCase()
}

function asLorebookArray({ lorebooks, lorebook }: CharacterLike): Lorebook[] {
  if (Array.isArray(lorebooks) && lorebooks.length) return lorebooks
  if (lorebook) return [lorebook]
  return []
}

function sortEntries(entries: LorebookEntry[] = []): LorebookEntry[] {
  return [...entries].sort((a, b) => {
    const byPriority = Number(b?.priority ?? 0) - Number(a?.priority ?? 0)
    if (byPriority) return byPriority
    const byPosition = Number(a?.position ?? 0) - Number(b?.position ?? 0)
    if (byPosition) return byPosition
    return Number(a?.id ?? 0) - Number(b?.id ?? 0)
  })
}

function buildScopedCorpus({ entry, messages, userMessage }: {
  entry: LorebookEntry
  messages: MessageLike[]
  userMessage: string
}): string {
  const scope = entry?.matchScope === 'user' || entry?.matchScope === 'character' ? entry.matchScope : 'both'
  const recentMessages = Array.isArray(messages) ? messages.slice(-RECENT_MATCH_MESSAGE_LIMIT) : []
  const textParts: string[] = []

  for (const message of recentMessages) {
    const isUser = Boolean(message?.is_user_author)
    if (scope === 'user' && !isUser) continue
    if (scope === 'character' && isUser) continue
    textParts.push(message?.content || '')
  }

  if ((scope === 'user' || scope === 'both') && typeof userMessage === 'string' && userMessage.trim()) {
    textParts.push(userMessage)
  }

  return normalizeLorebookMatchText(textParts.join('\n'))
}

function entryMatches({ entry, corpus, messageCount }: {
  entry: LorebookEntry
  corpus: string
  messageCount: number
}): boolean {
  if (!entry || entry.enabled === false) return false

  if (entry?.minMessages != null && Number(entry.minMessages) > Number(messageCount ?? 0)) {
    return false
  }

  const triggers = Array.isArray(entry?.triggers)
    ? entry.triggers.map((trigger) => trigger?.toString?.().trim?.() ?? '').filter(Boolean)
    : []

  if (!triggers.length) return false

  return triggers.some((trigger) => corpus.includes(normalizeLorebookMatchText(trigger)))
}

function formatLorebookEntry(entry: LorebookEntry): string {
  return `[Entry: ${entry.name}]\n${entry.content}`.trim()
}

export function buildLorebookSections({
  lorebooks = [],
  lorebook = null,
  messages = [],
  userMessage = '',
  contextSize = 4096,
  estimateTokens,
}: BuildLorebookSectionsOptions): BuildLorebookSectionsResult {
  const orderedLorebooks = asLorebookArray({ lorebooks, lorebook })
  if (!orderedLorebooks.length) {
    return { scenarioText: '', defaultText: '', matchedEntries: [] }
  }

  const effectiveMessageCount = Array.isArray(messages)
    ? messages.length + (typeof userMessage === 'string' && userMessage.trim() ? 1 : 0)
    : 0

  const eligibleEntries: LorebookEntry[] = []
  const matchedIds = new Set<string>()

  for (const lorebookItem of orderedLorebooks) {
    const entries = sortEntries(Array.isArray(lorebookItem?.entries) ? lorebookItem.entries : [])

    for (const entry of entries) {
      const entryId = `${Number(lorebookItem?.id ?? 0) || 'lorebook'}:${Number(entry?.id ?? 0) || `${entry?.name}-${entry?.position ?? 0}`}`
      if (matchedIds.has(entryId)) continue

      const corpus = buildScopedCorpus({ entry, messages, userMessage })
      if (!entryMatches({ entry, corpus, messageCount: effectiveMessageCount })) {
        continue
      }

      matchedIds.add(entryId)
      eligibleEntries.push(entry)
    }
  }

  if (!eligibleEntries.length) {
    return { scenarioText: '', defaultText: '', matchedEntries: [] }
  }

  const estimate = typeof estimateTokens === 'function'
    ? estimateTokens
    : (value: string) => Math.ceil((value || '').length / 4)

  const loreBudget = Math.max(0, Math.min(1200, Math.floor(Number(contextSize || 4096) * 0.25)))
  let usedTokens = 0
  const selectedEntries: LorebookEntry[] = []

  for (const entry of eligibleEntries) {
    const entryText = formatLorebookEntry(entry)
    const entryTokens = estimate(entryText)
    if (selectedEntries.length > 0 && usedTokens + entryTokens > loreBudget) {
      continue
    }
    usedTokens += entryTokens
    selectedEntries.push(entry)
  }

  const scenarioEntries: string[] = []
  const defaultEntries: string[] = []
  for (const entry of selectedEntries) {
    if (entry?.placement === 'scenario') {
      scenarioEntries.push(formatLorebookEntry(entry))
    } else {
      defaultEntries.push(formatLorebookEntry(entry))
    }
  }

  return {
    scenarioText: scenarioEntries.join('\n\n'),
    defaultText: defaultEntries.join('\n\n'),
    matchedEntries: selectedEntries,
  }
}
